import React, { useEffect, useState } from 'react';

import { getPDFPageCount, getPDFPageThumbnail } from '../../../utils/pdfToImage';

interface PDFPageSelectModalProps {
  isOpen: boolean;
  file: File | null;
  onSelectPages: (pageNumbers: number[]) => void;
  onClose: () => void;
  maxSelectCount?: number; // 最大選択数（未指定の場合は無制限）
}

export const PDFPageSelectModal: React.FC<PDFPageSelectModalProps> = ({
  isOpen,
  file,
  onSelectPages,
  onClose,
  maxSelectCount,
}) => {
  const [pageCount, setPageCount] = useState<number>(0);
  const [thumbnails, setThumbnails] = useState<Map<number, string>>(new Map());
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);

  // ページ数を取得（高速）
  useEffect(function loadPageCount() {
    if (!isOpen || !file) {
      setPageCount(0);
      setThumbnails(new Map());
      setSelectedPages([]);
      return;
    }

    let cancelled = false;

    async function loadCount() {
      setIsLoadingCount(true);
      setError(null);
      try {
        const count = await getPDFPageCount(file as File);

        if (cancelled) return;

        // 1ページのみの場合は自動的に選択して処理
        if (count === 1) {
          onSelectPages([1]);
          return;
        }

        setPageCount(count);
        setSelectedPages([1]);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load PDF:', e);
        setError('PDFの読み込みに失敗しました');
      } finally {
        if (!cancelled) {
          setIsLoadingCount(false);
        }
      }
    }

    loadCount();

    return () => {
      cancelled = true;
    };
  }, [isOpen, file, onSelectPages]);

  // サムネイルを順次読み込み（最初の4ページを優先）
  useEffect(function loadThumbnails() {
    if (!file || pageCount === 0) return;

    let cancelled = false;

    async function loadAllThumbnails() {
      // 最初の4ページを優先読み込み
      const priorityPages = [1, 2, 3, 4].filter(p => p <= pageCount);
      const remainingPages = Array.from({ length: pageCount }, (_, i) => i + 1)
        .filter(p => !priorityPages.includes(p));

      // 優先ページを先に読み込み
      for (const i of priorityPages) {
        if (cancelled) break;
        try {
          const pageInfo = await getPDFPageThumbnail(file as File, i, 0.3);
          if (cancelled) break;
          setThumbnails(prev => new Map(prev).set(i, pageInfo.thumbnailDataUrl));
        } catch (e) {
          console.error(`Failed to load thumbnail for page ${i}:`, e);
        }
      }

      // 残りのページを読み込み
      for (const i of remainingPages) {
        if (cancelled) break;
        try {
          const pageInfo = await getPDFPageThumbnail(file as File, i, 0.3);
          if (cancelled) break;
          setThumbnails(prev => new Map(prev).set(i, pageInfo.thumbnailDataUrl));
        } catch (e) {
          console.error(`Failed to load thumbnail for page ${i}:`, e);
        }
      }
    }

    loadAllThumbnails();

    return () => {
      cancelled = true;
    };
  }, [file, pageCount]);

  if (!isOpen) return null;

  function selectPage(pageNumber: number) {
    setSelectedPages((prev) => {
      if (prev.includes(pageNumber)) {
        // 選択済みなら解除
        return prev.filter((p) => p !== pageNumber);
      } else if (maxSelectCount === 1) {
        // 1枚制限の場合は切り替え
        return [pageNumber];
      } else if (!maxSelectCount || prev.length < maxSelectCount) {
        // 制限なし、または制限未満なら追加（ページ番号順にソート）
        return [...prev, pageNumber].sort((a, b) => a - b);
      }
      // 制限に達していたら何もしない
      return prev;
    });
  }

  function handleConfirm() {
    if (selectedPages.length > 0) {
      onSelectPages(selectedPages);
    }
  }

  return (
    <>
      {/* 背景オーバーレイ */}
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-50"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      />

      {/* モーダルコンテンツ */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div
          className="relative bg-white rounded-lg shadow-xl w-full flex flex-col"
          style={{ maxWidth: '700px', maxHeight: '85vh' }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* ヘッダー */}
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            編集するページを選択
          </h2>
          <p className="text-sm text-gray-500">
            編集したいページを選択してください{maxSelectCount ? `（最大${maxSelectCount}ページまで）` : ''}。
          </p>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoadingCount && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-2 animate-pulse">
                  <div className="bg-gray-200 rounded" style={{ aspectRatio: '297/210' }}></div>
                  <div className="flex justify-center mt-2">
                    <div className="bg-gray-200 rounded-full h-5 w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!isLoadingCount && !error && pageCount > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => {
                const isSelected = selectedPages.includes(pageNumber);
                const thumbnailUrl = thumbnails.get(pageNumber);
                return (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => selectPage(pageNumber)}
                    className={`relative border-2 rounded-lg p-2 transition-all ${
                      isSelected
                        ? 'border-blue-600 bg-blue-100'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* チェックマーク */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center z-10">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {/* サムネイルまたはプレースホルダー */}
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={`ページ ${pageNumber}`}
                        className="w-full h-auto rounded"
                      />
                    ) : (
                      <div
                        className="w-full bg-gray-100 rounded flex items-center justify-center animate-pulse"
                        style={{ aspectRatio: '297/210' }}
                      >
                        <span className="text-gray-400 text-sm">読み込み中...</span>
                      </div>
                    )}
                    <div
                      className={`absolute bottom-2 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-sm font-medium ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {pageNumber}ページ目
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* フッター */}
        <div
          className="bg-gray-50 px-6 rounded-b-lg"
          style={{ paddingTop: '16px', paddingBottom: '16px' }}
        >
          <div className="flex gap-3 justify-center" style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
            <button
              type="button"
              onClick={onClose}
              className="py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              style={{ flex: 1 }}
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoadingCount || selectedPages.length === 0}
              className="py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ flex: 1 }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PDFPageSelectModal;
